/**
 * 账单导入页（**D 域** · `/import` · `DR-051` · 批 86）
 *
 * 依据：`D域PRD` **§5.1**（上传与解析）· **§5.2**（预览 · 逐行确认 · 30 天撤销）·
 * **§5.3**（规则学习与管理）· `AC-D1.1`/`D1.11` · `AC-D2.1`/`D2.3`/`D2.9`/`D2.10` ·
 * `AC-D3.6`/`D3.7` · `AC-D5.1`/`D5.2`/`D5.3` · 铁律 38（UI 说「笔」不说「腿」）
 *
 * ## 🔴 本页**不解析、不算数、不判重**
 *
 * | 事 | 在哪 | 本页做什么 |
 * |---|---|---|
 * | 编码嗅探 / CSV / 列识别 / 行解析 | 共享内核 `nucleus/import.ts`（服务端调用） | 把 `File` 原样上传 |
 * | 去重（L1 不导入 · L2/L3 标注） | 同上 | 只显示 `duplicateText()` 的文案 |
 * | 分类建议与置信度 | 服务端 `suggestCategory()` | 只显示，并允许用户改（`AC-D5.2`） |
 * | 30 天可撤销窗口 | 服务端 `revocable` 字段（`AC-D2.9`） | **不拿浏览器时钟去比** |
 *
 * ## 🔴 撤销为什么要有二次确认
 *
 * 撤销会**删掉整批流水**（软删）。若其中还有**手工改过**的记录，服务端回 **409 + `details.editedCount`**
 * ⇒ 本页据此把对话框切成"危险确认"形态（`force=true` 由用户显式点出来），
 * 而不是端侧自己先猜"应该能撤"。
 */

import { useMemo, useRef, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import {
  CategoryKind,
  type AccountView,
  type CategoryView,
  type ImportBatchView,
  type ImportCommitResultView,
  type ImportPreviewResult,
  type ImportRuleView,
} from '@jiaying/shared';
import { ApiError } from '../api/http.js';
import {
  commitImport,
  deleteImportRule,
  listAccounts,
  listCategories,
  listImportBatches,
  listImportRules,
  previewImport,
  revokeImportBatch,
  updateImportRule,
} from '../api/endpoints.js';
import {
  IMPORT_ENCODING_CHOICES,
  buildCommitRequest,
  editedCountOfError,
  encodingParam,
  fileRejectReason,
  importCategoryOptions,
  importableCount,
  initialDecisions,
  revokeBlockedReason,
  revokeHint,
  type RowDecision,
} from '../features/import.js';
import { ImportPreviewTable } from '../ui/ImportPreviewTable.js';

export function ImportPage(): ReactElement {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const cats = useQuery({ queryKey: ['categories'], queryFn: listCategories });
  const batches = useQuery({ queryKey: ['importBatches'], queryFn: listImportBatches });
  const rules = useQuery({ queryKey: ['importRules'], queryFn: listImportRules });

  const [accountId, setAccountId] = useState('');
  const [encoding, setEncoding] = useState<string>('auto');
  const [rememberAll, setRememberAll] = useState(false);
  const [preview, setPreview] = useState<ImportPreviewResult | null>(null);
  const [decisions, setDecisions] = useState<ReadonlyMap<number, RowDecision>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ImportCommitResultView | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ImportBatchView | null>(null);
  const [forceCount, setForceCount] = useState<number | null>(null);

  const accountItems = useMemo(
    () => (accounts.data?.items ?? []).filter((a) => !a.hidden),
    [accounts.data],
  );
  // 默认选第一个可用账户（只在未选时生效 —— 用户选过就不覆盖）
  const effectiveAccountId = accountId !== '' ? accountId : (accountItems[0]?.id ?? '');

  const previewMut = useMutation({
    mutationFn: (file: File) =>
      previewImport(file, {
        accountId: effectiveAccountId,
        fileName: file.name,
        encoding: encodingParam(encoding),
      }),
    onSuccess: (p) => {
      setError(null);
      setDone(null);
      setPreview(p);
      const d = initialDecisions(p);
      if (rememberAll) {
        for (const r of p.rows) {
          const cur = d.get(r.gridRow);
          if (cur !== undefined && cur.categoryId !== null) {
            d.set(r.gridRow, { ...cur, remember: true });
          }
        }
      }
      setDecisions(d);
    },
    onError: (e) => {
      setPreview(null);
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const commitMut = useMutation({
    mutationFn: () => {
      if (preview === null) throw new Error('没有可提交的预览');
      return commitImport(buildCommitRequest(preview, decisions));
    },
    onSuccess: (res) => {
      setError(null);
      setDone(res);
      setPreview(null);
      setDecisions(new Map());
      void qc.invalidateQueries({ queryKey: ['importBatches'] });
      void qc.invalidateQueries({ queryKey: ['importRules'] });
      // 导入了流水 ⇒ 这些缓存全部失效（与手动记账同口径）
      void qc.invalidateQueries({ queryKey: ['transactions'] });
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
      void qc.invalidateQueries({ queryKey: ['report'] });
      void qc.invalidateQueries({ queryKey: ['journal'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const revokeMut = useMutation({
    mutationFn: (args: { batch: ImportBatchView; force: boolean }) =>
      revokeImportBatch(args.batch.id, args.force),
    onSuccess: () => {
      setRevokeTarget(null);
      setForceCount(null);
      setError(null);
      void qc.invalidateQueries({ queryKey: ['importBatches'] });
      void qc.invalidateQueries({ queryKey: ['transactions'] });
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
      void qc.invalidateQueries({ queryKey: ['report'] });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        const n = editedCountOfError(e.body.details);
        if (n !== null) {
          // 🔴 结构化旗标命中 ⇒ 切"危险确认"形态；**不改 `message`**
          setForceCount(n);
          return;
        }
      }
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const ruleUpdateMut = useMutation({
    mutationFn: (args: { rule: ImportRuleView; categoryId: string }) =>
      updateImportRule(args.rule.id, { categoryId: args.categoryId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['importRules'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const ruleDeleteMut = useMutation({
    mutationFn: (rule: ImportRuleView) => deleteImportRule(rule.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['importRules'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  function onPickFile(file: File | undefined): void {
    if (file === undefined) return;
    const reason = fileRejectReason(file);
    if (reason !== null) {
      setError(reason);
      return;
    }
    if (effectiveAccountId === '') {
      setError('请先选择这笔账单对应的账户');
      return;
    }
    previewMut.mutate(file);
  }

  function patchDecision(gridRow: number, next: RowDecision): void {
    const m = new Map(decisions);
    m.set(gridRow, next);
    setDecisions(m);
  }

  const categoryItems: readonly CategoryView[] = cats.data?.items ?? [];
  const ruleOptions = useMemo(
    () => [
      ...importCategoryOptions(categoryItems, CategoryKind.Expense),
      ...importCategoryOptions(categoryItems, CategoryKind.Income),
    ],
    [categoryItems],
  );

  const busy = previewMut.isPending || commitMut.isPending;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="import-page">
      {/* ── ① 上传 ── */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            导入账单
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            支持微信 / 支付宝导出的 CSV（**不需要解压后改编码**，服务端会自动识别 GBK）。
            预览阶段**不会写入任何数据**；确认后才落库，30 天内可整批撤销。
          </Typography>

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="import-account-label">账单对应的账户</InputLabel>
              <Select
                labelId="import-account-label"
                label="账单对应的账户"
                value={effectiveAccountId}
                onChange={(e) => setAccountId(e.target.value)}
                inputProps={{ 'data-testid': 'import-account' }}
              >
                {accountItems.map((a: AccountView) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="import-encoding-label">文件编码</InputLabel>
              <Select
                labelId="import-encoding-label"
                label="文件编码"
                value={encoding}
                onChange={(e) => setEncoding(e.target.value)}
                inputProps={{ 'data-testid': 'import-encoding' }}
              >
                {IMPORT_ENCODING_CHOICES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* 🔴 `data-testid` 挂外层 span：MUI 6 的 `Switch.inputProps` 类型不接受 `data-*`（TS2353） */}
            <Box component="span" data-testid="import-remember-all">
              <FormControlLabel
                control={
                  <Switch
                    checked={rememberAll}
                    onChange={(e) => setRememberAll(e.target.checked)}
                  />
                }
                label="对置信度高的行记住规则"
              />
            </Box>

            <input
              ref={fileRef}
              type="file"
              accept=".csv,.txt,text/csv"
              style={{ display: 'none' }}
              data-testid="import-file"
              onChange={(e) => {
                onPickFile(e.target.files?.[0]);
                // 允许重复选同一个文件（否则第二次选它不会触发 change）
                e.target.value = '';
              }}
            />
            <Button
              variant="contained"
              startIcon={<UploadFileOutlinedIcon />}
              disabled={busy || effectiveAccountId === ''}
              onClick={() => fileRef.current?.click()}
              data-testid="import-pick"
            >
              {busy ? '解析中…' : '选择 CSV 文件'}
            </Button>
            {busy && <CircularProgress size={20} />}
          </Stack>

          {error !== null && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="import-error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {done !== null && (
            <Alert severity="success" sx={{ mt: 2 }} data-testid="import-done">
              已导入 {done.imported} 笔
              {done.skippedDuplicates > 0 ? `，跳过重复 ${done.skippedDuplicates} 笔` : ''}
              {done.uncategorized > 0 ? `，其中 ${done.uncategorized} 笔未分类` : ''}
              {done.rulesLearned > 0 ? `，记住 ${done.rulesLearned} 条规则` : ''}
              。可在下方「导入历史」里整批撤销。
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* ── ② 预览 ── */}
      {preview !== null && (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="h6">确认这 {importableCount(preview)} 笔</Typography>
              <Button size="small" onClick={() => setPreview(null)} disabled={busy}>
                取消
              </Button>
            </Stack>
            <ImportPreviewTable
              preview={preview}
              categories={categoryItems}
              decisions={decisions}
              onChange={patchDecision}
              disabled={busy}
            />
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                disabled={busy || importableCount(preview) === 0}
                onClick={() => commitMut.mutate()}
                data-testid="import-commit"
              >
                确认导入 {importableCount(preview)} 笔
              </Button>
              <Typography variant="caption" color="text.secondary">
                未改动的行按服务端建议落库；未选分类的行记为「未分类」，之后在流水里补。
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ── ③ 导入历史 ── */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            导入历史
          </Typography>
          {batches.isPending ? (
            <CircularProgress size={20} />
          ) : (batches.data?.items.length ?? 0) === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid="import-empty-batches">
              还没有导入过账单。
            </Typography>
          ) : (
            <Stack divider={<Divider flexItem />}>
              {batches.data?.items.map((b) => {
                const blocked = revokeBlockedReason(b);
                return (
                  <Stack
                    key={b.id}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ py: 1 }}
                    data-testid={`import-batch-${b.id}`}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {b.fileName ?? '（未命名文件）'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {b.importedByName} · {b.itemCount} 笔
                        {b.editedCount > 0 ? ` · ${b.editedCount} 笔改过` : ''}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={b.status === 'revoked' ? '已撤销' : '已导入'}
                      color={b.status === 'revoked' ? 'default' : 'success'}
                      variant={b.status === 'revoked' ? 'outlined' : 'filled'}
                    />
                    <Button
                      size="small"
                      color="error"
                      disabled={blocked !== null || revokeMut.isPending}
                      title={blocked ?? undefined}
                      onClick={() => {
                        setForceCount(null);
                        setRevokeTarget(b);
                      }}
                      data-testid={`import-revoke-${b.id}`}
                    >
                      撤销
                    </Button>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* ── ④ 规则管理 ── */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            分类规则
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            导入时勾选「记住这条规则」后，同一商户下次会自动归到同一分类。
          </Typography>
          {rules.isPending ? (
            <CircularProgress size={20} />
          ) : (rules.data?.items.length ?? 0) === 0 ? (
            <Typography variant="body2" color="text.secondary" data-testid="import-empty-rules">
              还没有规则。导入时勾选「记住这条规则」就会出现在这里。
            </Typography>
          ) : (
            <Stack divider={<Divider flexItem />}>
              {rules.data?.items.map((r) => (
                <Stack
                  key={r.id}
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ py: 1 }}
                  data-testid={`import-rule-${r.id}`}
                >
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                    {r.normalizedMerchantName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    命中 {r.hitCount} 次
                  </Typography>
                  <Box sx={{ minWidth: 200 }}>
                    <Select
                      size="small"
                      fullWidth
                      value={r.categoryId}
                      onChange={(e) =>
                        ruleUpdateMut.mutate({ rule: r, categoryId: e.target.value })
                      }
                      inputProps={{ 'data-testid': `import-rule-cat-${r.id}` }}
                    >
                      {ruleOptions.map((o) => (
                        <MenuItem key={`${o.id}-${o.group}`} value={o.id}>
                          {o.group} · {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => ruleDeleteMut.mutate(r)}
                    aria-label="删除规则"
                    data-testid={`import-rule-del-${r.id}`}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* ── ⑤ 撤销确认 ── */}
      <Dialog open={revokeTarget !== null} onClose={() => setRevokeTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>撤销这次导入？</DialogTitle>
        <DialogContent>
          {revokeTarget !== null && (
            <>
              <Typography variant="body2">{revokeHint(revokeTarget)}</Typography>
              {forceCount !== null && (
                <Alert severity="warning" sx={{ mt: 2 }} data-testid="import-force-warning">
                  其中 {forceCount} 笔被手工修改过（改过分类 / 金额 / 标签）。继续撤销会**连它们一起删除**。
                </Alert>
              )}
              {/* 🔴 这里**不显示金额合计**：服务端未下发批次合计，端侧自己加 = 违反 `DR-023` */}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)}>取消</Button>
          <Button
            color="error"
            variant="contained"
            disabled={revokeMut.isPending}
            data-testid="import-revoke-confirm"
            onClick={() => {
              if (revokeTarget === null) return;
              revokeMut.mutate({ batch: revokeTarget, force: forceCount !== null });
            }}
          >
            {forceCount === null ? '确认撤销' : '仍然撤销'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
